import type { WorkflowStepStart } from "../../../../api/content/processDefinitions";
import type { StepPlan } from "../../../../api/content/stepPlans";
import type { StepProgressState } from "../../../../api/threads/threadDecoders";
import type { StepKind } from "../../../workflows/graph/stepKind";
import type { CurrentStep } from "../currentStep/stepFocus";

/**
 * How far the Workflow can run from the current step (Johannes, 28 Oct, F100): "Run all"
 * runs to the last step that starts by itself, a person's step asking its person on the way;
 * "Run to here" runs to one step once every step the run runs on the way has its inputs and
 * no person's step lies before it; the server stops the run there, as a notebook's "run to
 * here". A run skips the steps on request it doesn't need (PC-4): the server says which
 * (`skips`), so a skipped step's missing input holds nothing back, the current step's too.
 * Both are offered while the current step is ready, needs an input, already runs or waits for
 * its tool's approval, never while it waits for a person or failed. Module B's step plans say
 * what lacks an input; this only reads them.
 * Pure, so each rule is tested in one place.
 */
export interface RunReach {
  /** The steps after the current one that "Run to here" may run to. */
  readonly runTo: ReadonlySet<string>;
  /** The last step that starts by itself, when "Run all" may run to it; `null` otherwise. */
  readonly runAll: string | null;
}

export interface RunReachFacts {
  /** The runner's progress, in the Workflow's order. */
  readonly progress: Readonly<Record<string, StepProgressState>>;
  /** The current step as its typed values leave it. */
  readonly current: Pick<CurrentStep, "stepKey" | "position" | "state"> | null;
  readonly kindOf: (stepKey: string) => StepKind | null;
  readonly startOf: (stepKey: string) => WorkflowStepStart | null;
  /** Each step's plan; the current step's for its typed values. */
  readonly plans: ReadonlyMap<string, StepPlan>;
  /** For each step, the steps on request a run to it skips, from the server. */
  readonly skips: Readonly<Record<string, readonly string[]>>;
}

export const NO_REACH: RunReach = { runTo: new Set(), runAll: null };

const SETTLED: ReadonlySet<StepProgressState> = new Set<StepProgressState>(["done", "skipped"]);

export function runReach({ progress, current, kindOf, startOf, plans, skips }: RunReachFacts): RunReach {
  if (current === null || current.state === "waitingForYou" || current.state === "failed") return NO_REACH;
  const ahead = Object.keys(progress)
    .slice(current.position - 1)
    .filter((stepKey) => stepKey === current.stepKey || !SETTLED.has(progress[stepKey] ?? "not_due"));
  // A running step has what it needs, as has one whose turn waits for its tool's approval
  // (TR-B1-01); the steps after it are planned as they stand.
  const requested = current.state === "running" || current.state === "waitingForApproval";
  const lacks = (stepKey: string) => !(stepKey === current.stepKey && requested) && (plans.get(stepKey)?.reasons.length ?? 1) > 0;
  /** The steps a run to `target` runs, from the current one: those it doesn't skip. */
  const runs = (target: string) => {
    const skipped = new Set(skips[target] ?? []);
    return ahead.slice(0, ahead.indexOf(target) + 1).filter((stepKey) => !skipped.has(stepKey));
  };
  const runTo = new Set(
    ahead.filter(
      (target) => target !== current.stepKey && runs(target).every((stepKey) => !lacks(stepKey) && (stepKey === target || kindOf(stepKey) !== "person")),
    ),
  );
  const last = ahead.findLast((stepKey) => startOf(stepKey) === "automatic");
  const runAll = last === undefined || last === current.stepKey || runs(last).some(lacks) ? null : last;
  return { runTo, runAll };
}
